import { useEffect, useRef, useState } from 'react'
import { api } from './api'

// Live feed client (part 52).
//
// Why this is not the native EventSource: EventSource cannot set an Authorization header.
// Both the Go API and the Cloudflare Worker require a bearer token for /v1/live, so the hook
// consumes the same text/event-stream protocol with fetch + ReadableStream. That also lets
// us send Last-Event-ID explicitly and keep the bearer token out of the URL (and therefore
// out of access logs).
//
// The server closes each connection after 30 seconds on purpose; the loop below reconnects
// from the last id it saw, so the rotation is invisible and no event is delivered twice.

export const LIVE_KINDS = [
  'finding.created',
  'case.transition',
  'job.failed',
  'whistle.received',
] as const

export type LiveKind = (typeof LIVE_KINDS)[number]

export interface LiveEvent {
  id: number
  tenant_id: string
  kind: LiveKind
  payload: Record<string, unknown>
  created_at: string
}

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'error'

export interface LiveFeedState {
  events: LiveEvent[]
  status: LiveStatus
  error: string | null
}

const MAX_EVENTS = 200
const RECONNECT_MS = 750
const MAX_BACKOFF_MS = 8_000

interface TokenCache {
  token: string
  expiresAt: number
}

// The access token lives in api.ts's module scope and is not exported. A short-lived cache
// here is refreshed through the same HttpOnly refresh cookie the app already uses; sharing
// the in-flight promise keeps two mounts from consuming the rotating refresh token twice.
let tokenCache: TokenCache | null = null
let tokenInFlight: Promise<string> | null = null

async function refreshStreamToken(): Promise<string> {
  const res = await fetch('/v1/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  })
  if (!res.ok) throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่')
  const body = (await res.json()) as { data?: { access_token?: string } }
  const token = body.data?.access_token
  if (!token) throw new Error('ไม่ได้รับโทเคนสำหรับสตรีม')
  tokenCache = { token, expiresAt: Date.now() + 10 * 60_000 }
  return token
}

function streamToken(): Promise<string> {
  if (tokenCache && tokenCache.expiresAt > Date.now() + 30_000) {
    return Promise.resolve(tokenCache.token)
  }
  if (!tokenInFlight) {
    tokenInFlight = refreshStreamToken().finally(() => {
      tokenInFlight = null
    })
  }
  return tokenInFlight
}

function clearStreamToken() {
  tokenCache = null
  tokenInFlight = null
}

function isLiveKind(value: string): value is LiveKind {
  return (LIVE_KINDS as readonly string[]).includes(value)
}

function isLiveEvent(value: unknown): value is LiveEvent {
  if (typeof value !== 'object' || value === null) return false
  const e = value as Partial<LiveEvent>
  return (
    typeof e.id === 'number' &&
    Number.isFinite(e.id) &&
    e.id > 0 &&
    typeof e.kind === 'string' &&
    isLiveKind(e.kind) &&
    typeof e.created_at === 'string' &&
    typeof e.payload === 'object' &&
    e.payload !== null &&
    !Array.isArray(e.payload)
  )
}

/** Newest-first page of the feed. The SSE cursor starts at events[0].id. */
export async function recentLiveEvents(limit = 50): Promise<LiveEvent[]> {
  const n = Math.min(200, Math.max(1, Math.trunc(limit)))
  return api<LiveEvent[]>(`/v1/live/recent?limit=${n}`)
}

function handleFrame(frame: string, onEvent: (event: LiveEvent) => void) {
  let data = ''
  for (const line of frame.split(/\r?\n/)) {
    if (line === '' || line.startsWith(':')) continue
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    if (field !== 'data') continue
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '')
    data += (data ? '\n' : '') + value
  }
  if (!data) return
  try {
    const parsed: unknown = JSON.parse(data)
    if (isLiveEvent(parsed)) onEvent(parsed)
  } catch {
    // One malformed frame must not tear down a healthy stream; the next frame is independent.
  }
}

async function consume(body: ReadableStream<Uint8Array>, signal: AbortSignal, onEvent: (event: LiveEvent) => void) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      for (;;) {
        const match = /\r?\n\r?\n/.exec(buffer)
        if (!match) break
        const frame = buffer.slice(0, match.index)
        buffer = buffer.slice(match.index + match[0].length)
        handleFrame(frame, onEvent)
      }
    }
  } finally {
    void reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort)
      resolve()
    }, ms)
    function abort() {
      clearTimeout(timer)
      reject(new DOMException('aborted', 'AbortError'))
    }
    signal.addEventListener('abort', abort, { once: true })
  })
}

function statusOf(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'การเชื่อมต่อสตรีมขัดข้อง'
}

/**
 * Subscribes to the tenant live feed and keeps the newest 200 events in memory. The first
 * paint comes from /v1/live/recent; the stream is opened after that page is in hand so it
 * starts at the newest id and cannot replay the same rows.
 */
export function useLiveFeed(limit = 50): LiveFeedState {
  const [events, setEvents] = useState<LiveEvent[]>([])
  const [status, setStatus] = useState<LiveStatus>('connecting')
  const [error, setError] = useState<string | null>(null)
  const cursor = useRef(0)

  useEffect(() => {
    const abort = new AbortController()
    let stopped = false
    let attempt = 0

    const accept = (event: LiveEvent) => {
      if (event.id <= cursor.current) return
      cursor.current = event.id
      setEvents((previous) => [event, ...previous].slice(0, MAX_EVENTS))
    }

    const run = async () => {
      setStatus('connecting')
      setError(null)
      cursor.current = 0
      try {
        const seed = await recentLiveEvents(limit)
        if (stopped) return
        setEvents(seed)
        cursor.current = seed.reduce((max, event) => Math.max(max, event.id), 0)
      } catch (e) {
        if (stopped) return
        // History is a convenience; a live-only session still has value, so keep going.
        setError(statusOf(e))
      }

      while (!stopped) {
        try {
          const token = await streamToken()
          if (stopped) return
          const headers: Record<string, string> = {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
          }
          if (cursor.current > 0) headers['Last-Event-ID'] = String(cursor.current)

          const res = await fetch(`/v1/live?after=${cursor.current}`, {
            method: 'GET',
            headers,
            credentials: 'include',
            cache: 'no-store',
            signal: abort.signal,
          })
          if (res.status === 401) {
            clearStreamToken()
            throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่')
          }
          if (!res.ok) throw new Error(`สตรีมตอบกลับ ${res.status}`)
          if (!res.body) throw new Error('เบราว์เซอร์ไม่รองรับการอ่านสตรีม')

          attempt = 0
          setStatus('live')
          setError(null)
          await consume(res.body, abort.signal, accept)
          // A 30-second rotation lands here. Resume from cursor.current on the next pass.
          if (!stopped) setStatus('reconnecting')
        } catch (e) {
          if (stopped || abort.signal.aborted) return
          attempt += 1
          setStatus('reconnecting')
          setError(statusOf(e))
        }

        if (stopped) return
        try {
          await sleep(Math.min(RECONNECT_MS * Math.max(attempt, 1), MAX_BACKOFF_MS), abort.signal)
        } catch {
          return
        }
      }
    }

    void run()
    return () => {
      stopped = true
      abort.abort()
    }
  }, [limit])

  return { events, status, error }
}
